import { Editor, Transforms, Path, Text } from 'slate';

import { CustomText, ChipElement } from '../types';
import { conditionallyQuoteWrap } from '../utils';
import { isDescendantOfType } from './utils';

export const simpleChipHandlers = {
  // [PURPOSE]: Handles the conversion of a text node marked with 'convertToSimpleChip' into a 'simple-chip' element, primarily for quoted string values.
  convertToSimpleChip(editor: Editor, node: Node, path: Path) {
    if (!(Text.isText(node) && (node as CustomText).convertToSimpleChip)) {
      return false;
    }

    Editor.withoutNormalizing(editor, () => {
      const text = node.text.trim();
      // Create the chip
      const chipNode = {
        children: [{ text: conditionallyQuoteWrap(text) }],
        type: 'simple-chip',
        value: conditionallyQuoteWrap(text),
      };

      // Create text node with paren if needed
      const textAfterNode = { text: ' ' };

      // Replace the node with the chip and text
      Transforms.removeNodes(editor, { at: path });
      Transforms.insertNodes(editor, [chipNode as ChipElement, textAfterNode as CustomText], {
        at: path,
      });

      // Select after the chip
      const afterPath = Path.next(path);
      if (Editor.hasPath(editor, afterPath)) {
        Transforms.select(editor, Editor.end(editor, afterPath));
      } else {
        const pointAfter = Editor.after(editor, path);
        if (pointAfter) {
          Transforms.select(editor, pointAfter);
        } else {
          Transforms.select(editor, Editor.end(editor, path));
        }
      }
    });

    return true;
  },

  // [PURPOSE]: Detects if a text node contains a pattern suitable for a 'simple-chip' (currently, a quoted string of sufficient length) and marks it for conversion.
  detectSimpleChipPattern(editor: Editor, node: Node, path: Path) {
    if (
      !(
        Text.isText(node) &&
        node.text.trim() &&
        !node.convertToSimpleChip &&
        !node.isEditing &&
        !isDescendantOfType(editor, path, 'simple-chip')
      )
    ) {
      return false;
    }

    // Special handling for text with closing parenthesis
    const { text } = node;
    if (text.trim().length >= 3 && text.trim().startsWith('"') && text.trim().endsWith('"')) {
      Transforms.setNodes(editor, { convertToSimpleChip: true }, { at: path });

      return true;
    }

    return false;
  },
};

export default simpleChipHandlers;
